<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import LensMark from '@/components/lens/LensMark.vue';
import BnLensPanelChat from '@/components/lens/BnLensPanelChat.vue';
import { useLensPanel } from '@/composables/lens/useLensPanel';
import { lensPanelPage } from '@/components/lens/lensPanelPages';
import { useAuthStore } from '@/stores/auth';

const props = defineProps({
    // The app header's height: the panel starts directly below it.
    top: { type: Number, default: 58 },
});

const MIN_WIDTH = 320;
const DEFAULT_WIDTH = 400;
const WIDTH_KEY = 'bnLensPanelWidth';

const panel = useLensPanel();
const authStore = useAuthStore();
const route = useRoute();
const { smAndDown } = useDisplay();

const chat = ref(null);

// What the greeting offers on the page the user is on.
const page = computed(() => lensPanelPage(route));

// The panel lives in the layout, so navigating only closes it; the conversation is kept.
watch(
    () => route?.fullPath,
    () => panel.close(),
);

// ---- collapse when not focused --------------------------------------------------------------
const panelEl = ref(null);

// The header icon toggles the panel itself, so a press on it is not "somewhere else".
function isInside(target) {
    return target instanceof Node && (panelEl.value?.contains(target) || !!target.closest?.('[data-cy=lensPanelToggle]'));
}

// Capture phase: the page can't stop the press from reaching us. Focus covers keyboard moves away.
function collapseOnOutside(event) {
    if (!isInside(event.target)) panel.close();
}

function listen(on) {
    const method = on ? 'addEventListener' : 'removeEventListener';
    document[method]('pointerdown', collapseOnOutside, true);
    document[method]('focusin', collapseOnOutside, true);
}

watch(
    () => panel.open.value,
    (open) => {
        listen(open);
        if (!open) return;
        // Ready to type as soon as it opens.
        chat.value?.focus();
        // The page's guides, offered before the user types. Help is open to every Lens user.
        panel.loadPageGuides(route?.name);
    },
    { immediate: true },
);

onBeforeUnmount(() => listen(false));

function startOver() {
    panel.startOver();
    chat.value?.focus();
}

// ---- width ----------------------------------------------------------------------------------
function readWidth() {
    try {
        return Number(localStorage.getItem(WIDTH_KEY)) || DEFAULT_WIDTH;
    } catch {
        return DEFAULT_WIDTH;
    }
}

function maxWidth() {
    return Math.max(MIN_WIDTH, Math.round(window.innerWidth * 0.5));
}

function clampWidth(value) {
    return Math.round(Math.min(Math.max(value, MIN_WIDTH), maxWidth()));
}

// A width saved on a wider screen can exceed what this one allows.
const width = ref(clampWidth(readWidth()));
const isResizing = ref(false);
let dragStart = null;

const panelStyle = computed(() => ({
    top: `${props.top}px`,
    height: `calc(100% - ${props.top}px)`,
    width: smAndDown.value ? '100%' : `${width.value}px`,
}));

// The handle is on the left edge, so dragging left widens the panel. Pointer capture keeps the
// drag alive over the page without window listeners.
function startResize(event) {
    isResizing.value = true;
    dragStart = { x: event.clientX, width: width.value };
    event.currentTarget.setPointerCapture?.(event.pointerId);
}

function resize(event) {
    if (!isResizing.value) return;
    width.value = clampWidth(dragStart.width - (event.clientX - dragStart.x));
}

function stopResize(event) {
    if (!isResizing.value) return;
    isResizing.value = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    saveWidth();
}

function nudge(delta) {
    width.value = clampWidth(width.value + delta);
    saveWidth();
}

function saveWidth() {
    try {
        localStorage.setItem(WIDTH_KEY, String(width.value));
    } catch {
        // Width is a convenience; losing it is harmless.
    }
}
</script>

<template>
    <v-slide-x-reverse-transition>
        <!-- v-show, not v-if: the chat stays mounted while closed, so its draft and scroll survive. -->
        <aside
            v-show="panel.open.value"
            ref="panelEl"
            class="bn-lens-panel bg-surface elevation-8"
            :class="{ 'bn-lens-panel--resizing': isResizing }"
            :style="panelStyle"
            aria-label="Lens"
            data-cy="lensPanel"
        >
            <div
                v-if="!smAndDown"
                class="bn-lens-panel__handle"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize Lens panel"
                :aria-valuenow="width"
                :aria-valuemin="MIN_WIDTH"
                :aria-valuemax="maxWidth()"
                tabindex="0"
                data-cy="lensPanelResizer"
                @pointerdown.prevent="startResize"
                @pointermove="resize"
                @pointerup="stopResize"
                @pointercancel="stopResize"
                @keydown.left.prevent="nudge(40)"
                @keydown.right.prevent="nudge(-40)"
            />

            <div class="d-flex flex-column flex-grow-1" style="min-width: 0; min-height: 0">
                <div class="d-flex align-center ga-2 px-4 py-2 flex-grow-0">
                    <LensMark :size="28" />
                    <v-toolbar-title class="flex-grow-1 ma-0">Lens</v-toolbar-title>
                    <v-btn variant="text" color="primary" :disabled="!panel.messages.value.length" data-cy="lensPanelNewChat" @click="startOver">
                        <font-awesome-icon :icon="['far', 'plus']" />
                        <span class="ml-2">New chat</span>
                    </v-btn>
                    <v-btn icon variant="text" size="small" aria-label="Close Lens" title="Close Lens" data-cy="lensPanelClose" @click="panel.close()">
                        <font-awesome-icon :icon="['far', 'times']" size="lg" />
                    </v-btn>
                </div>
                <v-divider />

                <BnLensPanelChat
                    ref="chat"
                    class="flex-grow-1"
                    style="min-height: 0"
                    :messages="panel.messages.value"
                    :is-streaming="panel.isStreaming.value"
                    :first-name="authStore.firstName || ''"
                    :page="page"
                    :page-guides="panel.pageGuides.value"
                    @send="panel.send"
                    @stop="panel.stop()"
                    @new-chat="startOver"
                />
            </div>
        </aside>
    </v-slide-x-reverse-transition>
</template>

<style scoped>
/* Fixed over the page, below the header: the page underneath never moves or reflows. The z-index
   matches the nav drawers — above content, below dialogs. Not d-flex: its !important display would
   override v-show. */
.bn-lens-panel {
    position: fixed;
    right: 0;
    z-index: 1100;
    display: flex;
    max-width: 100%;
    border-left: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.bn-lens-panel__handle {
    /* Only the grip: a short grab area straddling the panel's left border at mid-height. */
    position: absolute;
    top: 50%;
    left: -6px;
    z-index: 1;
    width: 12px;
    height: 48px;
    transform: translateY(-50%);
    cursor: col-resize;
    touch-action: none;
    outline: none;
}

.bn-lens-panel__handle::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 5px;
    height: 32px;
    transform: translate(-50%, -50%);
    border-radius: 3px;
    background: rgba(var(--v-theme-on-surface), 0.25);
    transition: background-color 0.1s;
}

.bn-lens-panel__handle:hover::after,
.bn-lens-panel__handle:focus-visible::after,
.bn-lens-panel--resizing .bn-lens-panel__handle::after {
    background: rgb(var(--v-theme-primary));
}

/* Stops the drag from selecting page text as the pointer crosses it. */
.bn-lens-panel--resizing {
    user-select: none;
}
</style>
